/* eslint-disable */
import React, { useState, useEffect, useMemo } from 'react';
import {
	Container,
	Paper,
	Typography,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	FormGroup,
	FormControlLabel,
	Checkbox,
	Box,
	Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { skuTypeWiseGroupServices, notificationServices } from '@/Services';
import { PRODUCT_CLASSES } from '@/Constants/product.constants';

function SkuTypeWiseGroup() {
	const [groups, setGroups] = useState([]);
	const [searchQuery, setSearchQuery] = useState('');
	const [openDialog, setOpenDialog] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [selectedGroup, setSelectedGroup] = useState(null);
	
	// Form state
	const [groupName, setGroupName] = useState('');
	const [selectedSkuTypes, setSelectedSkuTypes] = useState({});
	const [formError, setFormError] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	// Load data
	const fetchGroups = async () => {
		setIsLoading(true);
		try {
			const res = await skuTypeWiseGroupServices.listSkuTypeWiseGroups();
			if (res && res.statusCode === 200) {
				setGroups(res.data || []);
			} else {
				console.error("Failed to fetch SkuTypeWiseGroups", res);
			}
		} catch (err) {
			console.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchGroups();
	}, []);

	// Handle add button click
	const handleAddClick = () => {
		setEditMode(false);
		setSelectedGroup(null);
		setGroupName('');
		// Reset selected types mapping
		const types = {};
		PRODUCT_CLASSES.forEach(c => {
			types[c.id] = false;
		});
		setSelectedSkuTypes(types);
		setFormError('');
		setOpenDialog(true);
	};

	// Handle edit button click
	const handleEditClick = (group) => {
		setEditMode(true);
		setSelectedGroup(group);
		setGroupName(group.name);
		
		const types = {};
		PRODUCT_CLASSES.forEach(c => {
			types[c.id] = (group.skuTypes || []).includes(c.id);
		});
		setSelectedSkuTypes(types);
		setFormError('');
		setOpenDialog(true);
	};

	// Handle checkbox change
	const handleCheckboxChange = (skuTypeId) => (e) => {
		setSelectedSkuTypes(prev => ({
			...prev,
			[skuTypeId]: e.target.checked
		}));
	};

	// Validate & Save
	const handleSave = async () => {
		const name = groupName.trim();
		if (!name) {
			setFormError('Group name is required.');
			return;
		}

		const skuTypesList = Object.keys(selectedSkuTypes).filter(key => selectedSkuTypes[key]);
		if (skuTypesList.length === 0) {
			setFormError('Please select at least one SKU Type.');
			return;
		}

		setFormError('');
		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: editMode ? 'Updating group...' : 'Creating group...',
		});

		try {
			let res;
			if (editMode && selectedGroup) {
				res = await skuTypeWiseGroupServices.updateSkuTypeWiseGroup({
					data: {
						id: selectedGroup.id,
						name,
						skuTypes: skuTypesList
					}
				});
			} else {
				res = await skuTypeWiseGroupServices.createSkuTypeWiseGroup({
					data: {
						name,
						skuTypes: skuTypesList
					}
				});
			}

			if (res && res.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: editMode ? 'Group updated successfully!' : 'Group created successfully!',
					config: { id: loadingId }
				});
				setOpenDialog(false);
				fetchGroups();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res.responseMessage || res.message || 'Operation failed',
					config: { id: loadingId }
				});
			}
		} catch (err) {
			notificationServices.generateNotification({
				type: 'error',
				message: err.message || 'An error occurred',
				config: { id: loadingId }
			});
		}
	};

	// Handle delete
	const handleDeleteClick = async (group) => {
		if (!window.confirm(`Are you sure you want to delete "${group.name}"?`)) {
			return;
		}

		const loadingId = notificationServices.generateNotification({
			type: 'loading',
			message: 'Deleting group...',
		});

		try {
			const res = await skuTypeWiseGroupServices.deleteSkuTypeWiseGroup({
				data: { id: group.id }
			});

			if (res && res.statusCode === 200) {
				notificationServices.generateNotification({
					type: 'success',
					message: 'Group deleted successfully!',
					config: { id: loadingId }
				});
				fetchGroups();
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res.responseMessage || res.message || 'Failed to delete group',
					config: { id: loadingId }
				});
			}
		} catch (err) {
			notificationServices.generateNotification({
				type: 'error',
				message: err.message || 'An error occurred',
				config: { id: loadingId }
			});
		}
	};

	// Filtered groups
	const filteredGroups = useMemo(() => {
		return groups.filter(g => 
			g.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
		);
	}, [groups, searchQuery]);

	// Translate SKU Type IDs to Names
	const getSkuTypeNames = (skuTypes) => {
		if (!skuTypes || !Array.isArray(skuTypes)) return '';
		return skuTypes
			.map(id => PRODUCT_CLASSES.find(c => c.id === id)?.name || id)
			.join(', ');
	};

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			<Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Typography variant="h5" component="h1" fontWeight="bold">
						SKU Type Groups Configuration
					</Typography>
					<Button
						variant="contained"
						color="primary"
						startIcon={<Add />}
						onClick={handleAddClick}
						sx={{ borderRadius: 1.5, px: 3 }}
					>
						Add SKU Type Group
					</Button>
				</Box>

				<Box mb={3}>
					<TextField
						fullWidth
						label="Search Groups by Name"
						variant="outlined"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						size="small"
					/>
				</Box>

				<TableContainer component={Box} sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
					<Table sx={{ minWidth: 650 }}>
						<TableHead sx={{ backgroundColor: '#f5f5f5' }}>
							<TableRow>
								<TableCell sx={{ fontWeight: 'bold' }}>Group Name</TableCell>
								<TableCell sx={{ fontWeight: 'bold' }}>SKU Types Included</TableCell>
								<TableCell align="right" sx={{ fontWeight: 'bold', pr: 4 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredGroups.length === 0 ? (
								<TableRow>
									<TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
										{isLoading ? 'Loading SKU Type Groups...' : 'No SKU Type Groups Configured'}
									</TableCell>
								</TableRow>
							) : (
								filteredGroups.map((group) => (
									<TableRow key={group.id} hover>
										<TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
											{group.name}
										</TableCell>
										<TableCell>{getSkuTypeNames(group.skuTypes)}</TableCell>
										<TableCell align="right" sx={{ pr: 2 }}>
											<IconButton color="primary" onClick={() => handleEditClick(group)} sx={{ mr: 1 }}>
												<Edit fontSize="small" />
											</IconButton>
											<IconButton color="error" onClick={() => handleDeleteClick(group)}>
												<Delete fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			{/* Create/Edit Dialog */}
			<Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
				<DialogTitle fontWeight="bold">
					{editMode ? 'Edit SKU Type Group' : 'Add New SKU Type Group'}
				</DialogTitle>
				<DialogContent dividers>
					{formError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formError}
						</Alert>
					)}
					<Box mb={3}>
						<TextField
							autoFocus
							fullWidth
							label="Group Name"
							variant="outlined"
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
							required
							size="small"
						/>
					</Box>
					<Typography variant="subtitle2" gutterBottom fontWeight="bold" color="text.secondary">
						Select SKU Types:
					</Typography>
					<FormGroup sx={{ pl: 1 }}>
						{PRODUCT_CLASSES.map(cls => (
							<FormControlLabel
								key={cls.id}
								control={
									<Checkbox
										checked={!!selectedSkuTypes[cls.id]}
										onChange={handleCheckboxChange(cls.id)}
										name={cls.id}
										color="primary"
									/>
								}
								label={cls.name}
							/>
						))}
					</FormGroup>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={() => setOpenDialog(false)} color="inherit" sx={{ fontWeight: 500 }}>
						Cancel
					</Button>
					<Button onClick={handleSave} color="primary" variant="contained" sx={{ px: 3, borderRadius: 1 }}>
						Save Group
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

export default SkuTypeWiseGroup;
